import IHistoryData from '../DataObject/History/IHistoryData';
import IHistoryService from '../Service/IHistoryService';
import IHistoryDetailData from '../DataObject/History/IHistoryDetailData';
import DataQuery, { SortOrder } from '../../../../../common/scripts/Toolkit/Helper/DataQuery';
import Model from '../../../../../common/scripts/Toolkit/Helper/Model';
import { ONE_DAY, TimeRangeOption, TIME_RANGE_OPTION_VALUE_MAP } from '../../UI/TimeRangeDropdown';

enum EventType {
    TIME_RANGE_CHANGED = 'TIME_RANGE_CHANGED',
    DATA_LIST_UPDATED = 'DATA_LIST_UPDATED',
    IS_REQUESTING_DATA_LIST_STATE_CHANGED = 'IS_REQUESTING_DATA_LIST_STATE_CHANGED',
}

export default class HistoryModel extends Model {
    public static readonly EVENT_TYPE = EventType;

    protected _dataQuery: DataQuery<IHistoryData>;
    protected readonly _machineQueryMap: { [machineID: string]: DataQuery<IHistoryData> } = {};
    protected readonly _detailMap: { [transactionID: string]: IHistoryDetailData } = {};

    protected _queryMachineID = -1;
    protected _queryStartTime = 0;
    protected _queryEndTime = 0;
    protected _queryTimeRange: TimeRangeOption = TimeRangeOption.TODAY;
    protected _queryTimeRangeValue: number = TIME_RANGE_OPTION_VALUE_MAP[this._queryTimeRange];

    constructor(
        protected readonly _historyService: IHistoryService,
        protected readonly chunkSize: number = DataQuery.DEFAULT_CHUNK_SIZE,
    ) {
        super();
        this._dataQuery = this.getQuery();
        // transport DataQuery events to HistoryModel events
        this._dataQuery.on(DataQuery.EVENT_TYPE.DATA_LIST_UPDATED, () => {
            this.emit(EventType.DATA_LIST_UPDATED);
        });
        this._dataQuery.on(DataQuery.EVENT_TYPE.IS_REQUESTING_STATE_CHANGED, () => {
            this.emit(EventType.IS_REQUESTING_DATA_LIST_STATE_CHANGED);
        });
    }

    public get noMoreData() {
        return this._dataQuery.noMoreData;
    }

    public get dataList() {
        const currentDateTime = new Date().setHours(0, 0, 0, 0);
        return this._dataQuery.dataList.filter((data) => data && this._checkIfDataWithinTimeRange(data, currentDateTime));
    }

    public get isRequestingDataList() {
        return this._dataQuery.isRequesting;
    }

    public init(): void {}

    public updateQueryTimeRange(newTimeRange: TimeRangeOption) {
        if (this._queryTimeRange === newTimeRange) {
            return;
        }
        // might have more data after time range enlarged
        const newTimeRangeValue = TIME_RANGE_OPTION_VALUE_MAP[newTimeRange];
        if (newTimeRangeValue > this._queryTimeRangeValue) {
            // might have more data after time range changed
            this.resetNoMoreData();
            this._dataQuery.resetSyncState();
        }
        this._queryTimeRange = newTimeRange;
        this._queryTimeRangeValue = newTimeRangeValue;
        this._updateQueryStartTime();
        this.emit(HistoryModel.EVENT_TYPE.TIME_RANGE_CHANGED);
    }

    public updateQueryEndTime() {
        this._queryEndTime = Date.now();
        this._updateQueryStartTime();
        Object.values(this._machineQueryMap).forEach((query) => query.resetSyncState());
    }

    public async syncDataList() {
        if (!this._dataQuery) {
            this.switchToNewQuery();
        }
        await this._dataQuery.checkSyncState();
    }

    public async requestForNewData(additionalCount: number = this.chunkSize) {
        await this._dataQuery.requestForNewData(additionalCount);
    }

    public async getDetail(transactionID: string, roundID: number) {
        if (!this._detailMap[transactionID]) {
            this._detailMap[transactionID] = await this._queryDetail(transactionID, roundID);
        }
        return this._detailMap[transactionID];
    }

    protected switchToNewQuery() {
        if (this._dataQuery) {
            this._unregisterQueryEventListeners(this._dataQuery);
        }
        this._dataQuery = this.getQuery();
        this._registerQueryEventListeners(this._dataQuery);
    }

    protected resetNoMoreData() {
        Object.values(this._machineQueryMap).forEach((query) => query.resetNoMoreData());
    }

    protected getQuery() {
        return new DataQuery<IHistoryData>({
            query: (page, pageSize) => this._historyService.getHistoryData(page, pageSize, this._queryStartTime),
            getDataIndex: (data) => data.date.getTime(),
            isSameData: (data1, data2) => data1.transactionID === data2.transactionID && data1.roundID === data2.roundID,
            sortOrder: SortOrder.Descending,
            chunkSize: this.chunkSize,
        });
    }

    private async _queryDetail(transactionID: string, roundID: number) {
        return this._historyService.getHistoryDetailData(transactionID, roundID);
    }

    private _updateQueryStartTime() {
        this._queryStartTime = new Date(this._queryEndTime - this._queryTimeRangeValue).setHours(0, 0, 0, 0) + ONE_DAY;
    }

    private _checkIfDataWithinTimeRange(data: IHistoryData, currentDateTime: number): boolean {
        // reset time to 00:00:00 to compare date only
        const dateTimeOfData = new Date(data.timestamp).setHours(0, 0, 0, 0);
        return currentDateTime - dateTimeOfData < this._queryTimeRangeValue;
    }

    private _registerQueryEventListeners(query: DataQuery<IHistoryData>) {
        Object.values(DataQuery.EVENT_TYPE).forEach((eventType) => {
            query.on(eventType, this._transferQueryEvent.bind(this, eventType));
        });
    }

    private _unregisterQueryEventListeners(query: DataQuery<IHistoryData>) {
        Object.values(DataQuery.EVENT_TYPE).forEach((eventType) => {
            query.off(eventType);
        });
    }

    private _transferQueryEvent(...args: [string, ...any[]]) {
        this.emit(...args);
    }
}
